/**
 * SignUp Component
 *
 * Signup page for new organization registration.
 * Integrates with @cfleet/api-client useSignup hook for backend API calls.
 *
 * Flow:
 * 1. User fills in organization and admin details
 * 2. Form submits to POST /auth/signup via useSignup hook
 * 3. On success, navigate to /login with success state and pre-filled email
 * 4. User must verify email before logging in
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignup } from '@cfleet/api-client';
import {
  ArrowRight,
  Lock,
  Mail,
  ShieldCheck,
  Building,
  User,
  Phone,
  Briefcase,
  Truck,
  ChevronDown,
  AlertCircle,
} from 'lucide-react';

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const { mutate: signup, isPending } = useSignup();

  // Form state
  const [company, setCompany] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [industry, setIndustry] = useState('');
  const [fleetSize, setFleetSize] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    signup(
      {
        company,
        name,
        email,
        password,
        phone: phone || undefined,
        industry: (industry || undefined) as
          | 'logistics'
          | 'retail'
          | 'healthcare'
          | 'food'
          | 'service'
          | 'other'
          | undefined,
        fleet_size: (fleetSize || undefined) as '1-5' | '6-20' | '21-100' | '101+' | undefined,
      },
      {
        onSuccess: () => {
          // Navigate to login with success state and pre-filled email
          navigate('/login', {
            state: {
              signupSuccess: true,
              email,
            },
          });
        },
        onError: (err) => {
          setError(err instanceof Error ? err.message : 'An error occurred during signup');
        },
      }
    );
  };

  const handleSignInClick = () => {
    navigate('/login');
  };

  // Clear error when user starts typing
  const handleInputChange = (setter: React.Dispatch<React.SetStateAction<string>>) => {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      if (error) setError(null);
      setter(e.target.value);
    };
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 font-sans text-slate-900 relative overflow-hidden">
      {/* Background Decor */}
      <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-cfleet-100/50 to-transparent -z-10"></div>

      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden animate-in fade-in zoom-in-95 duration-500">
        <div className="p-8">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 bg-cfleet-600 rounded-lg flex items-center justify-center text-white text-sm font-bold shadow-lg shadow-cfleet-500/30">
                C
              </div>
              <span className="font-bold text-3xl text-slate-800 tracking-tight">cfleet</span>
            </div>
          </div>

          <div className="mb-8 text-center">
            <h2 className="text-xl font-bold text-slate-900">Start your 14-day trial</h2>
            <p className="text-sm text-slate-500 mt-2">
              No credit card required. Setup takes 2 minutes.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-3 bg-rose-50 border border-rose-200 rounded-lg flex items-start gap-2 animate-in fade-in slide-in-from-top-2">
              <AlertCircle size={18} className="text-rose-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-rose-700">{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="company"
                className="block text-xs font-bold text-slate-700 uppercase mb-1.5 tracking-wide"
              >
                Company Name
              </label>
              <div className="relative group">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-cfleet-500 transition-colors">
                  <Building size={18} />
                </div>
                <input
                  id="company"
                  type="text"
                  required
                  value={company}
                  onChange={handleInputChange(setCompany)}
                  disabled={isPending}
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cfleet-500/20 focus:border-cfleet-500 transition-all placeholder:text-slate-400 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Acme Logistics"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="name"
                className="block text-xs font-bold text-slate-700 uppercase mb-1.5 tracking-wide"
              >
                Admin Name
              </label>
              <div className="relative group">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-cfleet-500 transition-colors">
                  <User size={18} />
                </div>
                <input
                  id="name"
                  type="text"
                  required
                  value={name}
                  onChange={handleInputChange(setName)}
                  disabled={isPending}
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cfleet-500/20 focus:border-cfleet-500 transition-all placeholder:text-slate-400 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="John Doe"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="email"
                  className="block text-xs font-bold text-slate-700 uppercase mb-1.5 tracking-wide"
                >
                  Work Email
                </label>
                <div className="relative group">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-cfleet-500 transition-colors">
                    <Mail size={16} />
                  </div>
                  <input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={handleInputChange(setEmail)}
                    disabled={isPending}
                    className="w-full pl-9 pr-3 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cfleet-500/20 focus:border-cfleet-500 transition-all placeholder:text-slate-400 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="john@acme.com"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="phone"
                  className="block text-xs font-bold text-slate-700 uppercase mb-1.5 tracking-wide"
                >
                  Phone
                </label>
                <div className="relative group">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-cfleet-500 transition-colors">
                    <Phone size={16} />
                  </div>
                  <input
                    id="phone"
                    type="tel"
                    required
                    value={phone}
                    onChange={handleInputChange(setPhone)}
                    disabled={isPending}
                    className="w-full pl-9 pr-3 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cfleet-500/20 focus:border-cfleet-500 transition-all placeholder:text-slate-400 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="(555) 000-0000"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="industry"
                  className="block text-xs font-bold text-slate-700 uppercase mb-1.5 tracking-wide"
                >
                  Industry{' '}
                  <span className="text-[10px] font-normal lowercase text-slate-400 italic">
                    (optional)
                  </span>
                </label>
                <div className="relative group">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-cfleet-500 transition-colors pointer-events-none">
                    <Briefcase size={16} />
                  </div>
                  <select
                    id="industry"
                    aria-label="Industry"
                    value={industry}
                    onChange={handleInputChange(setIndustry)}
                    disabled={isPending}
                    className="w-full pl-9 pr-8 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cfleet-500/20 focus:border-cfleet-500 transition-all appearance-none font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">Select Industry</option>
                    <option value="logistics">Logistics & Transport</option>
                    <option value="retail">Retail & E-commerce</option>
                    <option value="healthcare">Healthcare & Pharma</option>
                    <option value="food">Food & Beverage</option>
                    <option value="service">Service & Maintenance</option>
                    <option value="other">Other</option>
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                    <ChevronDown size={14} />
                  </div>
                </div>
              </div>

              <div>
                <label
                  htmlFor="fleetSize"
                  className="block text-xs font-bold text-slate-700 uppercase mb-1.5 tracking-wide"
                >
                  Fleet Size{' '}
                  <span className="text-[10px] font-normal lowercase text-slate-400 italic">
                    (optional)
                  </span>
                </label>
                <div className="relative group">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-cfleet-500 transition-colors pointer-events-none">
                    <Truck size={16} />
                  </div>
                  <select
                    id="fleetSize"
                    aria-label="Fleet Size"
                    value={fleetSize}
                    onChange={handleInputChange(setFleetSize)}
                    disabled={isPending}
                    className="w-full pl-9 pr-8 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cfleet-500/20 focus:border-cfleet-500 transition-all appearance-none font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">Number of Vehicles</option>
                    <option value="1-5">1 - 5</option>
                    <option value="6-20">6 - 20</option>
                    <option value="21-100">21 - 100</option>
                    <option value="101+">101+</option>
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                    <ChevronDown size={14} />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-xs font-bold text-slate-700 uppercase mb-1.5 tracking-wide"
              >
                Password
              </label>
              <div className="relative group">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-cfleet-500 transition-colors">
                  <Lock size={18} />
                </div>
                <input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={handleInputChange(setPassword)}
                  disabled={isPending}
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cfleet-500/20 focus:border-cfleet-500 transition-all placeholder:text-slate-400 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="••••••••"
                />
              </div>
              <p className="text-[10px] text-slate-400 mt-2">
                Minimum 8 characters with at least one number.
              </p>
            </div>

            <button
              type="submit"
              disabled={isPending}
              className="w-full bg-cfleet-600 text-white py-3 rounded-lg text-sm font-bold shadow-lg shadow-cfleet-500/30 hover:bg-cfleet-700 hover:shadow-xl hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 mt-6 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isPending ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  Create Account & Start Trial <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>
        </div>

        <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 text-center">
          <p className="text-xs text-slate-500 font-medium">
            Already have an account?{' '}
            <button
              onClick={handleSignInClick}
              className="text-cfleet-600 font-bold hover:underline"
            >
              Sign In
            </button>
          </p>
        </div>
      </div>

      {/* Footer Info */}
      <div className="mt-8 text-center space-y-3">
        <div className="flex items-center justify-center gap-2 text-xs text-emerald-600 font-medium bg-emerald-50 px-3 py-1 rounded-full w-fit mx-auto border border-emerald-100">
          <ShieldCheck size={12} /> GDPR & HIPAA Compliant
        </div>
        <p className="text-xs text-slate-400">By signing up, you agree to our Terms of Service.</p>
      </div>
    </div>
  );
};

export default SignUp;
